import { useState, useEffect } from "react";
import { Spinner } from "@chakra-ui/react";
import { useAutoSaveContext } from "./AutoSaveContext";

type IndicatorSize = "xs" | "sm" | "md" | "lg";

interface AutoSaveIndicatorProps {
	/** Override: pass explicitly, or omit to read from AutoSaveProvider context */
	error?: string | null;
	/** Override: pass explicitly, or omit to read from AutoSaveProvider context */
	isSaving?: boolean;
	/** Override: pass explicitly, or omit to read from AutoSaveProvider context */
	lastSavedAt?: Date | null;
	size?: IndicatorSize;
}

const sizeClassMap: Record<IndicatorSize, string> = {
	xs: "text-xs",
	sm: "text-sm",
	md: "text-base",
	lg: "text-lg",
};

const formatTime = (date: Date): string => {
	return date.toLocaleTimeString("en-AU", {
		hour: "2-digit",
		minute: "2-digit",
		hour12: false,
	});
};

const formatRelative = (date: Date, now: Date): string => {
	const diffMs = now.getTime() - date.getTime();
	const diffSec = Math.floor(diffMs / 1000);

	if (diffSec < 10) {
		return "just now";
	}
	if (diffSec < 60) {
		return `${diffSec}s ago`;
	}

	const diffMin = Math.floor(diffSec / 60);
	if (diffMin === 1) {
		return "1 min ago";
	}
	return `${diffMin} min ago`;
};

export const AutoSaveIndicator: React.FC<AutoSaveIndicatorProps> = ({
	isSaving: isSavingProp,
	lastSavedAt: lastSavedAtProp,
	error: errorProp,
	size = "xs",
}) => {
	const ctx = useAutoSaveContext();

	// Props override context; context is the fallback
	const isSaving = isSavingProp ?? ctx?.isSaving ?? false;
	const lastSavedAt = lastSavedAtProp ?? ctx?.lastSavedAt ?? null;
	const error = errorProp ?? ctx?.error ?? null;

	const sizeClass = sizeClassMap[size];
	const [now, setNow] = useState(() => new Date());

	useEffect(() => {
		if (!lastSavedAt) {
			return;
		}
		const interval = setInterval(() => setNow(new Date()), 10000);
		setNow(new Date());
		return () => clearInterval(interval);
	}, [lastSavedAt]);

	if (error) {
		return (
			<span className={`inline-flex items-center gap-1.5 ${sizeClass} text-red-500`} data-testid="autosave-error">
				Save failed
			</span>
		);
	}

	if (isSaving) {
		return (
			<span className={`inline-flex items-center gap-1.5 ${sizeClass} text-gray-400`} data-testid="autosave-saving">
				<Spinner size="md" color="gray.400" />
				Saving...
			</span>
		);
	}

	if (lastSavedAt) {
		return (
			<span className={`inline-flex items-center gap-1.5 ${sizeClass} text-blue-500`} data-testid="autosave-saved">
				Last auto-saved at {formatTime(lastSavedAt)} ({formatRelative(lastSavedAt, now)})
			</span>
		);
	}

	return null;
};
